import type { ImageSources } from "@/components/ui/FadeImage";

/**
 * Subset an ImageSources map to only the variants appropriate for
 * small slots (thumbnails, small cards, ~340 px wide and below).
 *
 * Why: PageSpeed flagged 81 KiB of waste on the orlock cross-sell
 * cards because some images lack intermediate `card` / `card_2x`
 * variants in the CMS. With the full sources map passed to FadeImage,
 * the browser saw `thumbnail` (300×300) and `hero_2x` (2400×1600) and
 * — needing a variant ≥ display-size × DPR — picked the megapixel
 * hero. By filtering the map to thumbnail/card-tier variants only,
 * the browser never sees `hero` / `hero_2x` and falls back to the
 * largest small variant available (or the `src` fallback if none).
 *
 * Use everywhere FadeImage is rendered in a small slot — cards,
 * thumbnails, avatar-style images, anywhere width < ~600 px CSS.
 */
export function smallSlotSources(sources: ImageSources | undefined): ImageSources | undefined {
  if (!sources) return undefined;
  return {
    thumbnail: sources.thumbnail,
    thumbnail_2x: sources.thumbnail_2x,
    card: sources.card,
    card_2x: sources.card_2x,
    // Preserve AVIF variants for the same small-slot sizes so the
    // bandwidth win still applies to thumbnails + cards. Without
    // these, smallSlotSources would silently strip AVIF and force
    // the browser back to WebP for small slots.
    thumbnail_avif: sources.thumbnail_avif,
    thumbnail_2x_avif: sources.thumbnail_2x_avif,
    card_avif: sources.card_avif,
    card_2x_avif: sources.card_2x_avif,
  };
}

/**
 * Pick the best `src` fallback for a small-slot FadeImage. Used as the
 * <img>'s actual `src` attribute — what browsers without srcset support
 * download, and what shows up in PageSpeed's "image delivery" audit
 * when the responsive picker fails.
 *
 * Order: prefer card_2x → card → thumbnail_2x → thumbnail → original.
 */
export function smallSlotSrc(sources: ImageSources | undefined, fallback: string): string {
  return (
    sources?.card_2x ??
    sources?.card ??
    sources?.thumbnail_2x ??
    sources?.thumbnail ??
    fallback
  );
}

/**
 * Smallest usable variant, for slots of ~40–80 px — the search typeahead's
 * row icons and anything else that size.
 *
 * `smallSlotSrc` is the wrong tool there: it prefers `card_2x` (~680 px),
 * which is fine for a card but still ~40× the pixels a 40 px avatar needs.
 * The typeahead was worse again — it used the raw `product.image`, i.e. the
 * ORIGINAL upload, so opening the dropdown pulled a 2400×1536 photo per row
 * to paint a 40 px square.
 *
 * Order runs smallest-first and falls back up the ladder, because not every
 * CMS image has every variant generated.
 */
export function iconSlotSrc(sources: ImageSources | undefined, fallback: string): string {
  return (
    sources?.thumbnail ??
    sources?.thumbnail_2x ??
    sources?.card ??
    sources?.card_2x ??
    fallback
  );
}
